import { useQuery } from "@tanstack/react-query";
import { adminQueries } from "./admin-query";
import {
  CopyButton,
  PageHeader,
  PanelState,
  RefreshFailedNotice,
} from "./AdminUi";
import { Icon } from "./Icon";

export function ProviderPanel() {
  const providerQuery = useQuery(adminQueries.provider());
  // Only a failure with nothing cached is fatal; a failed background refetch
  // keeps the panel on screen.
  if (!providerQuery.data) {
    return (
      <PanelState
        error={providerQuery.isError}
        label="provider configuration"
      />
    );
  }

  const { provider } = providerQuery.data;
  return (
    <>
      <PageHeader
        action={
          <a
            className="button button--secondary"
            href={provider.endpoints.discovery}
            rel="noreferrer"
            target="_blank"
          >
            Open discovery
            <Icon name="external" size={15} />
          </a>
        }
        description="Discovery values, immutable protocol controls, and environment-owned defaults."
        eyebrow="Provider & security"
        title="OIDC configuration"
      />

      {providerQuery.isError && <RefreshFailedNotice />}

      <div className="provider-grid">
        <section className="panel-card panel-card--wide">
          <header>
            <div>
              <span className="eyebrow">Integration</span>
              <h2>Endpoints</h2>
            </div>
          </header>
          <dl className="endpoint-list">
            {Object.entries(provider.endpoints).map(([name, value]) => (
              <div key={name}>
                <dt>{name}</dt>
                <dd>
                  <a href={value} rel="noreferrer" target="_blank">
                    <code>{value}</code>
                  </a>
                  <CopyButton label={`Copy ${name} endpoint`} value={value} />
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">Security invariants</span>
              <h2>Enforced controls</h2>
            </div>
            <span className="panel-status panel-status--positive">
              Enforced
            </span>
          </header>
          <ul className="check-list">
            <li>Authorization Code flow only</li>
            <li>S256 PKCE is required</li>
            <li>Exact redirect URL matching</li>
            <li>RS256 ID and access tokens</li>
            <li>Public subject identifiers</li>
          </ul>
          <div className="notice notice--neutral compact-notice">
            Cloudflare Access profile: Authorization Code + S256 PKCE.
            Optional OAuth capabilities are not enabled because this
            integration does not require them. Implicit flow is intentionally
            unsupported.
          </div>
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">Signing keys</span>
              <h2>Key publication</h2>
            </div>
            <span
              className={`panel-status ${provider.signingKeys.published > 1 ? "panel-status--positive" : "panel-status--warning"}`}
            >
              {provider.signingKeys.published > 1
                ? "Rotation ready"
                : "1 active key"}
            </span>
          </header>
          <dl className="facts-list">
            <div>
              <dt>Active kid</dt>
              <dd>
                <code>{provider.signingKeys.currentKid ?? "Unavailable"}</code>
              </dd>
            </div>
            <div>
              <dt>Published keys</dt>
              <dd>{provider.signingKeys.published}</dd>
            </div>
            <div>
              <dt>Algorithm</dt>
              <dd>{provider.invariants.signingAlgorithm}</dd>
            </div>
          </dl>
          <p className="supporting-copy">
            Private signing material remains in encrypted Worker secrets and is
            never exposed to this dashboard.
          </p>
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">Defaults</span>
              <h2>Token lifetimes</h2>
            </div>
          </header>
          <dl className="facts-list">
            <div>
              <dt>Access token</dt>
              <dd>{provider.defaultTtlSeconds.accessToken}s</dd>
            </div>
            <div>
              <dt>Authorization code</dt>
              <dd>{provider.defaultTtlSeconds.authorizationCode}s</dd>
            </div>
            <div>
              <dt>Login transaction</dt>
              <dd>{provider.defaultTtlSeconds.authTransaction}s</dd>
            </div>
          </dl>
          <p className="supporting-copy">
            These defaults seed new clients. Each application has bounded
            lifetime overrides.
          </p>
        </section>

        <section className="panel-card">
          <header>
            <div>
              <span className="eyebrow">Admin access</span>
              <h2>Cloudflare Access</h2>
            </div>
          </header>
          <dl className="facts-list">
            <div>
              <dt>Managed by</dt>
              <dd>{provider.adminAccess.managedBy}</dd>
            </div>
            <div>
              <dt>Verification</dt>
              <dd>External; not inferred as healthy</dd>
            </div>
            <div>
              <dt>Team domain</dt>
              <dd>
                <code>{provider.adminAccess.teamDomain}</code>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </>
  );
}
